import appManager from '@app/appManager';
import * as Telecom from '@servicetitan/telecom-rtcs-client';

/** Derived from AgentWrapupStartedEvent.stats - not directly exported from package */
type AgentStats = NonNullable<Telecom.AgentWrapupStartedEvent['stats']>;
import {
  ServiceSessionEventType,
  ServiceSessionRecoveryEvent,
} from '../../types/ServiceEvents';
import ServiceUser, { userStatsFromAgentStats } from '../../types/ServiceUser';
import requireUser from '../../lib/requireUser';
import log from '../../lib/log';
import isValidUser from '../../lib/isValidUser';
import {
  ServiceUserUtils,
  userStatsLastActionTimeFix,
} from '../../lib/serviceUserUtils';
import { EventAvailabilityState } from '../../lib/eventAvailabilityState';
import { UserSetAvailabilityStatus } from '@api/generated/graphql';
import { setUserStatus } from '../../lib/userStatus';

export interface OnAgentRecoveryStartedProps {
  user: ServiceUser;
  agentId: string;
  available: boolean;
  wrapupAllowedMs: number;
  stats?: AgentStats;
  conversationId: string;
  conversationSid: string;
  timestamp: string;
}

export const onAgentRecoveryStarted = async (
  props: OnAgentRecoveryStartedProps,
) => {
  const {
    user,
    agentId,
    available,
    wrapupAllowedMs,
    stats,
    conversationId,
    conversationSid,
    timestamp,
  } = props;
  user.available = available;
  ServiceUserUtils.setEventActivityStatus(
    user,
    EventAvailabilityState.WrapupTime,
  );
  // If user is available set status to Available, if user not Available
  if (available && !ServiceUserUtils.isUserAvailable(user)) {
    setUserStatus({
      userId: user.id,
      status: UserSetAvailabilityStatus.Available,
    });
  }

  user.recoveryTime = wrapupAllowedMs;
  user.capacity = 0;

  if (stats) {
    user.stats = userStatsFromAgentStats(stats);

    userStatsLastActionTimeFix({ user, timestamp: timestamp });
  }

  appManager.events.emit<ServiceSessionRecoveryEvent>(
    ServiceSessionEventType.RECOVERY_STARTED,
    {
      sessionId: conversationId,
      sessionSid: conversationSid,
      agentId: agentId,
      userId: user.id,
      timestamp: timestamp,
      recoveryAllowedMs: wrapupAllowedMs,
    },
  );
};

/**
 * The wrapup start event indicates the start of wrap-up time.
 * Which is a configurable (per-queue) amount of time after handling
 * a session that the recipient will not be offered additional sessions.
 * @param event
 */
async function handleAgentRecoveryStarted(
  event: Telecom.AgentWrapupStartedEvent,
) {
  const {
    agentId,
    available,
    wrapupAllowedMs,
    stats,
    conversationId,
    conversationSid,
    timestamp,
  } = event;

  if (!isValidUser(event.agentId)) {
    return;
  }

  const user = await requireUser(event.agentId);

  log('rtcs:agent:wrapup_started:' + user?.name, event);

  onAgentRecoveryStarted({
    user,
    agentId,
    available,
    wrapupAllowedMs,
    stats,
    conversationId,
    conversationSid,
    timestamp,
  });
}

export default handleAgentRecoveryStarted;
